import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import Theme from '../common/NekoTheme';

/**
 * @param {string} id
 * @param {string} name
 * @param {string} value
 * @param {string} description
 * @param {string} placeholder
 */
const StyledNekoTextArea = Styled(props => {
  const { id, name, value = '', description, placeholder = '', avoidOnEnterWithShift = false, onEnter = null, readOnly = false, 
    maxLength = null, className, style, textAreaStyle = {}, countable = false, ...rest } = props;

  const [ newValue, setNewValue ] = useState(value);
  const [ count, setCount ] = useState(0);
  const [ pressShift, setPressShift ] = useState(false);
  const isControlled = !!props.onChange;

  const keydownFunction = useCallback((e) => {
    setPressShift(e.shiftKey);
  }, []);
  const keyupFunction = useCallback(() => {
    setPressShift(false);
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", keydownFunction, false);
    document.addEventListener("keyup", keyupFunction, false);

    return () => {
      document.removeEventListener("keydown", keydownFunction, false);
      document.removeEventListener("keyup", keyupFunction, false);
    }
  }, []);

  useEffect(() => {
    if (!isControlled) {
      setNewValue(value);
    }
    if (countable) {
      setCount(value.length);
    }
  }, [ value, countable ]);

  const onChange = useCallback((e) => {
    const newValue = maxLength ? e.target.value.substr(0, maxLength): e.target.value;
    if (isControlled) { 
      props.onChange(newValue, id);
    }
    else {
      setNewValue(newValue, id);
    }
  }, [maxLength, props.onChange]);

  const onKeyPress = (e) => {
    if (onEnter && !e.shiftKey && e.key === 'Enter') {
      if (avoidOnEnterWithShift && pressShift) return;

      e.preventDefault();
      props.onEnter(e.target.value, id);
    }
  };

  const onBlur = (e) => {
    if (props.onBlur && value !== e.target.value) {
      props.onBlur(e.target.value, id);
    }
  };

  return (
    <div className={[className].join(' ')} style={style}>
      <div className="neko-textarea-container">
        <textarea className="neko-textarea" rows={6} {...rest} id={id} name={name} spellCheck="false" placeholder={placeholder} 
          onChange={onChange} onKeyPress={onKeyPress} onBlur={onBlur} readOnly={readOnly} style={textAreaStyle}
          value={isControlled ? value : newValue} />
        {countable && <p className="neko-textarea-count">{count} / {maxLength} characters</p>}
      </div>
      {description && <p className="neko-input-description">{description}</p>}
    </div>
  );
})`
  .neko-textarea-container {
    position: relative;
  }
  .neko-textarea {
    font-family: ${Theme.fontFamily};
    font-size: ${Theme.fontSizeText};
    border: 2px solid ${Theme.blue};
    box-sizing: border-box;
    background: rgb(0 124 186 / 10%);
    padding: 5px 10px;
    width: 100%;

    &::placeholder {
      color: rgba(0, 0, 0, 0.25);
    }

    :focus {
      background-color: white;
    }

    :read-only {
      background-color: ${Theme.gray};
    }
  }
  .neko-textarea-count {
    color: rgba(${Theme.blackRGB}, 0.4);
    font-size: 11px;
    margin: 0;
    position: absolute;
    right: 13px;
    bottom: 10px;
  }

  .neko-input-description {
    font-family: ${Theme.fontFamily};
    font-size: ${Theme.fontSizeSmall};
    line-height: 14px;
    margin-top: 8px;
    margin-bottom: 0;
  }
`;

const NekoTextArea = (props) => {
  return (<StyledNekoTextArea {...props} />);
};

NekoTextArea.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  description: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onEnter: PropTypes.func,
  onBlur: PropTypes.func,
  readOnly: PropTypes.bool,
  avoidOnEnterWithShift: PropTypes.bool
};

NekoTextArea.defaultProps = {
  id: undefined,
  name: undefined,
  value: '',
  description: undefined,
  placeholder: '',
  onChange: undefined,
  onEnter: undefined,
  onBlur: undefined,
  readOnly: false,
  avoidOnEnterWithShift: false
};

export { NekoTextArea };
